import { useQuery } from '@tanstack/react-query';
import { X, Building2, Send, CheckCheck, Eye, MousePointer } from 'lucide-react';
import { format } from 'date-fns';
import { emailsApi } from '../lib/api';
import { EmailStatusBadge, getStatusLevel } from './EmailStatusBadge';
import { IconButton } from './Button';
import type { Email, EmailStatus } from '../types';

interface EmailDetailDrawerProps {
  emailId: string | null;
  onClose: () => void;
}

const timelineSteps: { status: EmailStatus; icon: typeof Send; label: string }[] = [
  { status: 'sent', icon: Send, label: 'Sent' },
  { status: 'delivered', icon: CheckCheck, label: 'Delivered' },
  { status: 'opened', icon: Eye, label: 'Opened' },
  { status: 'clicked', icon: MousePointer, label: 'Clicked' },
];

export function EmailDetailDrawer({ emailId, onClose }: EmailDetailDrawerProps) {
  const { data: email, isLoading } = useQuery({
    queryKey: ['email', emailId],
    queryFn: () => emailsApi.get(emailId!),
    enabled: !!emailId,
  });

  if (!emailId) return null;

  const emailData = email as Email | undefined;
  const currentLevel = emailData ? getStatusLevel(emailData.status) : 0;
  const isBounced = emailData?.status === 'bounced' || emailData?.status === 'failed';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 animate-fade-in"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-[var(--asi-bg)] shadow-xl z-50 animate-slide-in overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--asi-bg-tertiary)]">
          <h2 className="text-lg font-semibold text-[var(--asi-text)]">Email Details</h2>
          <IconButton icon={<X className="w-5 h-5" />} label="Close" onClick={onClose} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--asi-accent)]" />
            </div>
          ) : !emailData ? (
            <div className="text-center py-12 text-[var(--asi-text-secondary)]">
              Email not found
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Status & Subject */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <EmailStatusBadge status={emailData.status} />
                  {emailData.direction && (
                    <span className="text-xs text-[var(--asi-text-muted)] capitalize">
                      {emailData.direction}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-[var(--asi-text)]">
                  {emailData.subject || '(No subject)'}
                </h3>
              </div>

              {/* Contact Info */}
              {emailData.contact && (
                <div className="p-4 bg-[var(--asi-bg-secondary)] rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {emailData.contact.name?.[0] || '?'}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-[var(--asi-text)]">
                        {emailData.contact.name}
                      </div>
                      {emailData.contact.company && (
                        <div className="flex items-center gap-1 text-sm text-[var(--asi-text-secondary)]">
                          <Building2 className="w-3 h-3" />
                          {emailData.contact.company}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Email Metadata */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-sm text-[var(--asi-text-secondary)] w-16">From</span>
                  <span className="text-sm text-[var(--asi-text)]">
                    {emailData.from_address || '-'}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm text-[var(--asi-text-secondary)] w-16">To</span>
                  <span className="text-sm text-[var(--asi-text)]">
                    {emailData.to_address || '-'}
                  </span>
                </div>
                {emailData.cc_addresses && emailData.cc_addresses.length > 0 && (
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-[var(--asi-text-secondary)] w-16">CC</span>
                    <span className="text-sm text-[var(--asi-text)]">
                      {emailData.cc_addresses.join(', ')}
                    </span>
                  </div>
                )}
              </div>

              {/* Status Timeline */}
              {!isBounced && (
                <div>
                  <h4 className="text-sm font-medium text-[var(--asi-text-secondary)] mb-3">
                    Delivery Timeline
                  </h4>
                  <div className="flex items-center gap-1">
                    {timelineSteps.map((step, index) => {
                      const stepLevel = getStatusLevel(step.status);
                      const isCompleted = currentLevel >= stepLevel;
                      const isCurrent = currentLevel === stepLevel;
                      const Icon = step.icon;

                      return (
                        <div key={step.status} className="flex items-center flex-1">
                          <div className="flex flex-col items-center flex-1">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isCompleted
                                  ? 'bg-[var(--asi-accent)] text-[var(--asi-text)]'
                                  : 'bg-[var(--asi-bg-tertiary)] text-[var(--asi-text-muted)]'
                              } ${isCurrent ? 'ring-2 ring-[var(--asi-accent)] ring-offset-2 ring-offset-[var(--asi-bg)]' : ''}`}
                            >
                              <Icon className="w-4 h-4" />
                            </div>
                            <span
                              className={`text-[10px] mt-1 ${
                                isCompleted
                                  ? 'text-[var(--asi-text)]'
                                  : 'text-[var(--asi-text-muted)]'
                              }`}
                            >
                              {step.label}
                            </span>
                          </div>
                          {index < timelineSteps.length - 1 && (
                            <div
                              className={`h-0.5 flex-1 -mx-1 ${
                                currentLevel > stepLevel
                                  ? 'bg-[var(--asi-accent)]'
                                  : 'bg-[var(--asi-bg-tertiary)]'
                              }`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-[var(--asi-text-secondary)]">
                  Activity
                </h4>
                <div className="space-y-2 text-sm">
                  {emailData.created_at && (
                    <div className="flex items-center justify-between text-[var(--asi-text-secondary)]">
                      <span>Created</span>
                      <span>{format(new Date(emailData.created_at), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                  )}
                  {emailData.sent_at && (
                    <div className="flex items-center justify-between text-[var(--asi-text-secondary)]">
                      <span>Sent</span>
                      <span>{format(new Date(emailData.sent_at), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                  )}
                  {emailData.opened_at && (
                    <div className="flex items-center justify-between text-[var(--asi-text-secondary)]">
                      <span>Opened</span>
                      <span>{format(new Date(emailData.opened_at), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                  )}
                  {emailData.clicked_at && (
                    <div className="flex items-center justify-between text-[var(--asi-text-secondary)]">
                      <span>Clicked</span>
                      <span>{format(new Date(emailData.clicked_at), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Email Body */}
              {(emailData.body_text || emailData.body_html) && (
                <div>
                  <h4 className="text-sm font-medium text-[var(--asi-text-secondary)] mb-3">
                    Content
                  </h4>
                  <div className="p-4 bg-[var(--asi-bg-secondary)] rounded-xl">
                    {emailData.body_html ? (
                      <div
                        className="prose prose-sm max-w-none text-[var(--asi-text)]"
                        dangerouslySetInnerHTML={{ __html: emailData.body_html }}
                      />
                    ) : (
                      <pre className="text-sm text-[var(--asi-text)] whitespace-pre-wrap font-sans">
                        {emailData.body_text}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
